import type { CommandPaletteTarget } from 'features/commandPalette/utils/commandPaletteTargets'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import type { OpenCommandPaletteTargetOptions } from './useOpenCommandPaletteTarget'
export const POINTER_GRACE_MS = 500

interface ModifierKeys {
  metaKey: boolean
  ctrlKey: boolean
}

const hasNewTabModifier = (event: ModifierKeys): boolean =>
  event.metaKey || event.ctrlKey

const isOnRow = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest('[cmdk-item]') !== null

interface UseCommandPaletteInteractionsOptions {
  isOpen: boolean
  close: () => void
  openTarget: (
    target: CommandPaletteTarget,
    options: OpenCommandPaletteTargetOptions
  ) => void
  openNewAgenticChat: ((options: { question: string }) => void) | undefined
}

/**
 * Owns the palette's typed text, highlighted row, and the modifier-aware way
 * a selection, a question or a lost focus closes it. The `remember*` and
 * `close*` handlers belong on the cmdk root.
 */
export function useCommandPaletteInteractions({
  isOpen,
  close,
  openTarget,
  openNewAgenticChat,
}: UseCommandPaletteInteractionsOptions) {
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState('')
  const [closesInstantly, setClosesInstantly] = useState(false)
  const clickWantsNewTab = useRef(false)
  const lastPointerDownAt = useRef(0)
  const hasOpenedNewTabRef = useRef(false)
  const trimmedQuery = query.trim()

  // The dialog stays mounted through its exit animation,
  // and the fading frame should keep what the user typed.
  useLayoutEffect(() => {
    if (!isOpen) return
    setQuery('')
    setHighlighted('')
    setClosesInstantly(false)
    clickWantsNewTab.current = false
    hasOpenedNewTabRef.current = false
  }, [isOpen])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) close()
    },
    [close]
  )

  // Close before navigating so the palette never repaints with the new
  // page's results on its way out.
  const selectFromRow = useCallback(
    (target: CommandPaletteTarget) => {
      const wantsNewTab = clickWantsNewTab.current
      clickWantsNewTab.current = false
      const inNewTab = wantsNewTab && !hasOpenedNewTabRef.current
      if (inNewTab) hasOpenedNewTabRef.current = true
      setClosesInstantly(true)
      close()
      openTarget(target, { inNewTab })
    },
    [close, openTarget]
  )

  const ask = useCallback(() => {
    if (!openNewAgenticChat || !trimmedQuery) return
    setClosesInstantly(true)
    close()
    openNewAgenticChat({ question: trimmedQuery })
  }, [close, openNewAgenticChat, trimmedQuery])

  const rememberEnterModifier = useCallback(
    (
      event: ModifierKeys & {
        key: string
        repeat?: boolean
        preventDefault?: () => void
      }
    ) => {
      if (event.key !== 'Enter') return
      if (event.repeat) {
        event.preventDefault?.()
        return
      }
      clickWantsNewTab.current = hasNewTabModifier(event)
    },
    []
  )

  const rememberClickModifier = useCallback(
    (event: ModifierKeys & { target: EventTarget | null }) => {
      clickWantsNewTab.current =
        isOnRow(event.target) && hasNewTabModifier(event)
    },
    []
  )

  const rememberPointerDown = useCallback(() => {
    lastPointerDownAt.current = Date.now()
  }, [])

  // Browser extensions that bind Escape (vim-style ones leave insert mode by
  // blurring the field) swallow the key before the page sees it, leaving the
  // palette open with focus on nothing; treat that as the close it was meant
  // to be. Pointer-driven blurs and window switches are left alone.
  const closeIfFocusWasLost = useCallback(
    (event: { relatedTarget: EventTarget | null }) => {
      if (event.relatedTarget !== null) return
      if (Date.now() - lastPointerDownAt.current < POINTER_GRACE_MS) return
      if (!document.hasFocus()) return
      close()
    },
    [close]
  )

  return {
    query,
    setQuery,
    trimmedQuery,
    highlighted,
    setHighlighted,
    closesInstantly,
    handleOpenChange,
    selectFromRow,
    ask,
    rememberEnterModifier,
    rememberClickModifier,
    rememberPointerDown,
    closeIfFocusWasLost,
  }
}
